import { useEffect } from 'react'
import { useContainerWidth } from '@fs/zion-ui'
import { ACTIONS, useDSCDispatch, useDSCState } from '../../../state/DSCStore'
import { getURLParams } from '../../helpers/url_helper'

const stationModeConfig = {
  showHeader: false,
  showFooter: false,
  appLayout: {
    fullWidth: true,
  },
}

const HIDDEN_CHROME = { showHeader: false, showFooter: false }

// Each rule reads the current chrome state and returns the one action that
// would bring it in line, or null when it already is. Kept as plain functions
// so the effect below is just "apply whichever rules bind here".

// Where am I from hides the global chrome on narrow screens. It never turns
// the chrome back on - master's restore branch was unreachable, and a screen
// that hid its header on purpose keeps it hidden.
const wcfChromeRule = ({ showHeader, isMobile }) =>
  showHeader && isMobile ? { type: ACTIONS.SET_HEADER_FOOTER, data: HIDDEN_CHROME } : null

// Famous Relatives lays out at content width, not full width.
const mfrLayoutRule = ({ appLayout }) =>
  appLayout?.fullWidth ? { type: ACTIONS.SET_APPLAYOUT, appLayout: { fullWidth: false } } : null

// The landing page shows both header and footer at full width.
const landingChromeRule = ({ showHeader, showFooter, hideHeaderFooterFromQueryString }) =>
  !hideHeaderFooterFromQueryString && (!showHeader || !showFooter)
    ? { type: ACTIONS.SET_HEADER_FOOTER, data: { showHeader: true, showFooter: true, appLayout: { fullWidth: true } } }
    : null

// ?hidehf hides both, whatever the experience.
const hideChromeRule = ({ showHeader, showFooter, hideHeaderFooterFromQueryString }) =>
  hideHeaderFooterFromQueryString && (showHeader || showFooter)
    ? { type: ACTIONS.SET_HEADER_FOOTER, data: HIDDEN_CHROME }
    : null

// The experiences with chrome rules of their own, applied in WEB only.
const EXPERIENCE_CHROME_RULES = {
  wcf: wcfChromeRule,
  mfr: mfrLayoutRule,
  landing: landingChromeRule,
}

export default function useDSCChromeStateSynchronization({ activeExperienceId, appMode }) {
  const DSCDispatch = useDSCDispatch()
  const { appLayout, hasSetToStationOrKiosk, showFooter, showHeader } = useDSCState()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({
    default: true,
    xl: false,
  })
  const { hidehf: hideHeaderFooterFromQueryString } = getURLParams(window.location.search)
  const fullWidth = appLayout?.fullWidth

  useEffect(() => {
    if (appMode === 'STATION' && !hasSetToStationOrKiosk) {
      DSCDispatch({
        type: ACTIONS.SET_STATION_MODE,
        data: stationModeConfig,
      })
    }
  }, [DSCDispatch, appMode, hasSetToStationOrKiosk])

  useEffect(() => {
    const chrome = { showHeader, showFooter, appLayout: { fullWidth }, isMobile, hideHeaderFooterFromQueryString }
    const experienceRule = appMode === 'WEB' ? EXPERIENCE_CHROME_RULES[activeExperienceId] : null

    ;[experienceRule?.(chrome), hideChromeRule(chrome)].filter(Boolean).forEach((action) => DSCDispatch(action))
  }, [
    DSCDispatch,
    activeExperienceId,
    fullWidth,
    appMode,
    hideHeaderFooterFromQueryString,
    isMobile,
    showFooter,
    showHeader,
  ])
}
